import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { IIssueLocation } from '@d-orca/shared/trusstor';
import { useReducedMotion } from '@hooks/useReducedMotion';
import { fetchFloorViewSvg } from '@apis/storage';
import { CloseIcon, MapPinIcon } from '@utils/icons';
import { SingleSelect } from '@components/ui/SingleSelect/SingleSelect';
import { FloorPlanCanvas, type IAreaLabel } from '../../IssuesPage/Dialog/FloorPlanCanvas';
import { groupLocationsByFloor, type IFloorLocationView } from './groupLocationsByFloor';
import styles from './drawer.module.css';

interface IIssueLocationsPanelProps {
  siteId: string;
  locations: IIssueLocation[];
  onClose: () => void;
  /** Drives the enter/exit animation. When it flips to false the panel plays
      its close animation, then calls `onExited` so the parent can unmount it. */
  open?: boolean;
  onExited?: () => void;
}

/**
 * The "set locations" plan as a read-only companion panel docked to the LEFT of
 * the issue drawer (same idiom as the create/edit Add-Location panel). Kept out
 * of the drawer's vertical flow so it never pushes the comment thread down — the
 * user can read the plan and chat at the same time. Reuses the Add-Location
 * renderer (`FloorPlanCanvas`) with editing off. Renders nothing without
 * locations.
 */
export function IssueLocationsPanel({
  siteId,
  locations,
  onClose,
  open = true,
  onExited,
}: IIssueLocationsPanelProps) {
  const { t } = useTranslation();
  const [activeFloorId, setActiveFloorId] = useState<string | null>(null);
  // The area highlighted by hovering/tapping a chip or the plan — links the two.
  const [activeAreaId, setActiveAreaId] = useState<string | null>(null);
  const prefersReducedMotion = useReducedMotion();

  const floors = useMemo(() => groupLocationsByFloor(locations), [locations]);
  const active = floors.find((floor) => floor.floorId === activeFloorId) ?? floors[0];

  // Read the latest onExited without making it an effect dependency (it's a
  // fresh closure each parent render), matching the codebase's useEffectEvent idiom.
  const notifyExited = useEffectEvent(() => onExited?.());
  // While closing: unmount immediately when there's nothing to animate — no
  // floor to render (no <aside> to fire animationend) or reduced-motion (no
  // animation runs) — otherwise the parent would stay stuck mounted.
  useEffect(() => {
    if (open) return;
    if (!active || prefersReducedMotion) notifyExited();
  }, [open, active, prefersReducedMotion]);

  const floorOptions = useMemo(
    () => floors.map((floor) => ({ id: floor.floorId, label: floor.floorNick })),
    [floors],
  );

  if (!active) return null;

  return (
    <aside
      className={`${styles.locCompanion} ${open ? styles.locCompanionOpen : styles.locCompanionClosing}`}
      aria-label={t('activities.drawer.location')}
      onClick={(event) => event.stopPropagation()}
      onAnimationEnd={(event) => {
        if (!open && event.target === event.currentTarget) onExited?.();
      }}
    >
      <div className={styles.locCompanionHead}>
        <span className={styles.locCompanionTitle}>
          <MapPinIcon size={15} />
          {t('activities.drawer.location')}
        </span>
        <button
          type="button"
          className={styles.locCompanionClose}
          onClick={onClose}
          aria-label={t('activities.drawer.closeLocations')}
        >
          <CloseIcon size={18} />
        </button>
      </div>

      {floors.length > 1 && (
        <div className={styles.locPickerBar}>
          <SingleSelect
            value={active.floorId}
            onChange={(floorId) => {
              setActiveFloorId(floorId);
              setActiveAreaId(null);
            }}
            options={floorOptions}
            placeholder={active.floorNick}
            ariaLabel={t('activities.drawer.floor')}
            elevated
          />
        </div>
      )}

      <FloorLocationCanvas
        key={active.floorId}
        siteId={siteId}
        floor={active}
        activeAreaId={activeAreaId}
        onActiveAreaChange={setActiveAreaId}
      />

      {active.areas.length > 0 && (
        <ul className={styles.locAreas}>
          {active.areas.map((area) => (
            <li key={area.areaId}>
              <button
                type="button"
                className={`${styles.locAreaChip} ${activeAreaId === area.areaId ? styles.locAreaChipActive : ''}`}
                onMouseEnter={() => setActiveAreaId(area.areaId)}
                onMouseLeave={() => setActiveAreaId(null)}
                onFocus={() => setActiveAreaId(area.areaId)}
                onBlur={() => setActiveAreaId(null)}
                onClick={() => setActiveAreaId(area.areaId)}
              >
                <span className={styles.locAreaSwatch} aria-hidden="true" />
                {area.areaNick}
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

interface IFloorLocationCanvasProps {
  siteId: string;
  floor: IFloorLocationView;
  activeAreaId: string | null;
  onActiveAreaChange: (areaId: string | null) => void;
}

/** Fetches one floor's plan SVG and mounts the read-only canvas over it. */
function FloorLocationCanvas({ siteId, floor, activeAreaId, onActiveAreaChange }: IFloorLocationCanvasProps) {
  const { t } = useTranslation();
  const { data: svg, isLoading, isError } = useQuery({
    // Same key as the Add-Location dialog so the SVG is served from cache
    // when the user has just opened the plan there.
    queryKey: ['storage', 'floor-view', siteId, floor.floorId],
    queryFn: ({ signal }) => fetchFloorViewSvg(siteId, floor.floorId, signal),
    staleTime: 5 * 60_000,
  });

  // Memoised so FloorPlanCanvas's effects don't re-subscribe each render.
  const areaLabels = useMemo<IAreaLabel[]>(
    () => floor.areas.map((area) => ({ areaId: area.areaId, label: area.areaNick })),
    [floor.areas],
  );

  return (
    <div className={styles.locCanvas}>
      {isLoading && <span className={styles.locMsg}>{t('issues.action.loading')}</span>}
      {isError && <span className={styles.locMsg}>{t('issues.field.floorPlanLoadError')}</span>}
      {/* fetchFloorViewSvg returns null on a 404 — say so rather than show a blank box. */}
      {!isLoading && !isError && svg === null && (
        <span className={styles.locMsg}>{t('issues.field.noFloorPlanAvailable')}</span>
      )}
      {svg && (
        // Pins are anchored to the rendered plan inside FloorPlanCanvas, so no
        // aspect-fitting is needed here — the plan can letterbox and pins stay put.
        <FloorPlanCanvas
          svg={svg}
          pinsByArea={floor.pinsByArea}
          selectedAreaIds={floor.selectedAreaIds}
          areaLabels={areaLabels}
          activeAreaId={activeAreaId}
          onActiveAreaChange={onActiveAreaChange}
        />
      )}
    </div>
  );
}
